import { Box, Typography, alpha, lighten, useTheme } from '@mui/material';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { notify } from 'reapop';
import AlertDialog from 'src/components/AlertDialog';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { NavigationButtons } from 'src/pages/home/tab_panel';
import { submitQuestionnaireAns } from 'src/store/questionnaire/questionnaireService';
import { setTabIndex } from 'src/store/questionnaire/questionnaireSlice';
import Header from './Header';
import Sidebar from './Sidebar';

interface SidebarLayoutProps {
  children?: ReactNode;
}

const SidebarLayout: FC<SidebarLayoutProps> = () => {
  const theme = useTheme();
  const params = useParams();
  const dispatch = useAppDispatch();
  const targetRef = useRef<HTMLDivElement>(null);
  const { tabIndex, questionnaires, status, message, client, docs_to_upload, isAuthenticated } = useAppSelector((state) => state.applications);
  const [confirmSubmission, setConfirmSubmission] = useState({ state: false, asDraft: false });

  useEffect(() => {
    if (status === 'fulfilled') {
      dispatch(notify(message, 'success'))
    } else if (status === 'rejected') {
      dispatch(notify(message, 'error'))
    }
    // eslint-disable-next-line
  }, [status]);


  useEffect(() => {
    if (targetRef.current != null) {
      targetRef.current.scrollTop = 0
    }
  }, [tabIndex])

  const handleSave = ({ asDraft }: { asDraft: boolean }) => {
    if (!asDraft) {
      setConfirmSubmission({ state: true, asDraft });
    } else {
      dispatch(submitQuestionnaireAns({
        clientId: params.client_id!, questionnaires, isDraft: asDraft, files: docs_to_upload
      }))
    }
  }

  return (
    <>
      {confirmSubmission.state && <AlertDialog
        alert_type='info'
        state={status}
        title='CONFIRM ACTION'
        onProceed={() => {
          setConfirmSubmission({ state: false, asDraft: false });
          dispatch(submitQuestionnaireAns({
            clientId: params.client_id!, questionnaires, isDraft: confirmSubmission.asDraft, files: docs_to_upload
          }))
        }}
        onClose={() => setConfirmSubmission({ state: false, asDraft: false })}
        body={<Typography>Are you sure you want to submit?</Typography>}
        open={confirmSubmission.state}
      />}
      <Box
        sx={{
          flex: 1,
          height: '100%',

          '.MuiPageTitle-wrapper': {
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.trueWhite[5]
                : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 1px 0 ${alpha(
                  lighten(theme.colors.primary.main, 0.7),
                  0.15
                )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(
                  theme.colors.alpha.black[100],
                  0.1
                )}, 0px 5px 12px -4px ${alpha(
                  theme.colors.alpha.black[100],
                  0.05
                )}`
          }
        }}
      >
        <Header />
        <Sidebar />
        <Box
          sx={{
            position: 'relative',
            zIndex: 5,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            flexGrow: 1,
            flex: 1,
            pt: `${theme.header.height}`,
            [theme.breakpoints.up('lg')]: {
              ml: `${theme.sidebar.width}`
            }
          }}
        >
          <Box display="block" ref={targetRef} height={'100%'} overflow={'auto'}>
            <Outlet />
          </Box>
          {client?.kyc_section_titles.length !== 0 &&
            <NavigationButtons
              status={status}
              isAuthenticated={isAuthenticated}
              isLastForm={tabIndex === questionnaires.length - 1}
              onSave={() => handleSave({ asDraft: true })}
              onPrevious={() => tabIndex > 0 && dispatch(setTabIndex(tabIndex - 1))}
              onNext={() => {
                if (tabIndex === questionnaires.length - 1) {
                  handleSave({ asDraft: false });
                } else {
                  tabIndex + 1 <= questionnaires.length - 1 && dispatch(setTabIndex(tabIndex + 1))
                }
              }}
            />
          }
        </Box>
      </Box>
    </>
  );
};

export default SidebarLayout;
